/* iter179 — Clock-out warning modal when the tech still has unfinished billing.
   - Calls /api/timeclock/out-precheck on open.
   - If today_count > 0, shows the unfinished job list + two buttons:
       Finish Billing (deep-links to first unfinished job)
       Clock Out With Reason (opens the reason picker)
   - "Other" requires a free-text explanation.
   - On confirm, POST /api/timeclock action=out with the reason payload.
*/
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Wrench, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnfinishedBillingModal = ({ open, onClose, precheck, onClockedOut, onFinishBilling }) => {
  const [reason, setReason] = useState('');
  const [reasonOther, setReasonOther] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!precheck) return null;
  const items = precheck.unfinished_today || [];
  const reasons = precheck.reasons || [];

  const handleClockOutWithReason = async () => {
    setError('');
    if (reason === 'other' && !reasonOther.trim()) {
      setError('Please type a brief explanation for "Other".');
      return;
    }
    setSubmitting(true);
    try {
      // Billing is a follow-up concern — NEVER a payroll blocker. We send the
      // reason only if the tech happened to pick one; a reason is NOT required
      // to stop the payroll clock. Billing Missing records are preserved.
      const payload = { action: 'out' };
      if (reason) {
        payload.unfinished_billing_reason = reason;
        payload.unfinished_billing_reason_other = reasonOther.trim();
        payload.unfinished_billing_job_ids = items.map((i) => i.id);
      }
      await axios.post(`${API}/timeclock`, payload, { withCredentials: true });
      setReason('');
      setReasonOther('');
      onClockedOut?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Clock-out failed.');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="unfinished-billing-modal"
        className="bg-[#141414] border border-red-500/40 text-white max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Unfinished Billing
          </DialogTitle>
          <DialogDescription className="text-[#C9C9C9]">
            You still have billing that needs attention on <strong className="text-white">{items.length}</strong> job{items.length === 1 ? '' : 's'}.
            You can finish it now, or clock out anyway — your billing will stay flagged for follow-up. Clocking out is never blocked.
          </DialogDescription>
        </DialogHeader>

        <div data-testid="unfinished-billing-jobs" className="max-h-48 overflow-y-auto space-y-2">
          {items.map((j) => (
            <div
              key={j.id}
              data-testid={`unfinished-job-${j.id}`}
              className="bg-[#0A0A0A] border border-[#262626] rounded p-3 text-sm"
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white font-bold truncate">{j.customer_name || '(no customer)'}</p>
                  <p className="text-[#A3A3A3] text-xs truncate">{j.shift_title || j.service_type || '—'}</p>
                </div>
                <p className="text-[#737373] text-[11px] whitespace-nowrap">
                  {j.date} {j.start_time && <>· {j.start_time}</>}
                </p>
              </div>
              {(j.last_action_step || j.last_invoice_status) && (
                <p className="text-[#525252] text-[11px] mt-1">
                  Last step: {j.last_action_step || '—'} · invoice: {j.last_invoice_status || '—'}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-[#262626] pt-4 space-y-3">
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Optional — add a reason for the office</Label>
            <div data-testid="unfinished-billing-reasons" className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {reasons.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  data-testid={`reason-${r.value}`}
                  onClick={() => setReason(r.value)}
                  className={`text-left text-sm px-3 py-2 rounded border transition-colors ${
                    reason === r.value
                      ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-bold'
                      : 'bg-[#0A0A0A] text-white border-[#262626] hover:border-[#D4AF37]'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {reason === 'other' && (
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Briefly explain *</Label>
              <Textarea
                data-testid="reason-other-text"
                rows={2}
                value={reasonOther}
                onChange={(e) => setReasonOther(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                placeholder="e.g. waiting on parts confirmation, customer asked us to come back tomorrow"
              />
            </div>
          )}
          {error && (
            <p data-testid="unfinished-billing-error" className="text-red-300 text-sm">{error}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-3">
          <Button
            data-testid="finish-billing-btn"
            onClick={() => onFinishBilling?.(items[0])}
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold flex-1"
            disabled={submitting || items.length === 0}
          >
            {items.length > 1 ? (
              <><FileText className="w-4 h-4 mr-1.5" /> Open First Unfinished Job</>
            ) : (
              <><Wrench className="w-4 h-4 mr-1.5" /> Finish Billing</>
            )}
          </Button>
          <Button
            data-testid="clock-out-anyway-btn"
            onClick={handleClockOutWithReason}
            variant="outline"
            disabled={submitting}
            className="border-red-500/40 text-red-300 hover:bg-red-500/10 flex-1"
          >
            {submitting ? 'Clocking out…' : 'Clock Out Anyway'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnfinishedBillingModal;
